import styles from './reset-pass.scss';
import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import classnames from 'classnames/bind';

import LoginBasicBox from '@/components/auth/login-basic-box/login-basic-box';
import { Button, Form } from 'react-bootstrap';
import { useLoginPasswordMutation } from '@/services/auth.api';
import PasswordInput from '@/components/PasswordInput';
import * as yup from 'yup';
import {
  PASSWORD_REGEX,
  REDIRECT_SEARCH_PARAM_KEY,
  TEMPORARY,
} from '@/constants/system';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import _ from 'lodash';
import { dispatch } from '@/store';
import { addToast, ToastType } from '@/store/system';
import PasswordStrengthIndicator from '@/components/password-strength-indicator/password-strength-indicator';
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';
import { LoginResponse } from '@/services/api.types';
import { advanceLoginStep } from '@/pages/Auth/login-step';
import { mapRequestError } from '@/services/error.utils';

const cx = classnames.bind(styles);

type PasswordForm = {
  pass: string;
  confirmPass: string;
};

const defaultFormValue: PasswordForm = {
  pass: '',
  confirmPass: '',
};

// Forced-change (PWD) gate of the backend-enforced login transaction (ADR-03,
// batch 002). Reached from login / login-sms when the step response is
// `pending{next=STEP_PWD}` (temp password not yet changed). Submits the new
// password to the tmp-token-scoped endpoint, then advances the transaction off
// the response body. Legacy V1/V2 switch, initialAccess/activate and the
// password-expiry variant are gone (expiry forced-change was cut in ADR-03).
const ResetPass = () => {
  const navigate = useNavigate();
  const { lt } = useLevvelTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPass, setCurrentPass] = useState('');
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const account = (state as any)?.account;

  const [loginPassword, { isLoading }] = useLoginPasswordMutation();

  const schema = yup
    .object({
      pass: yup
        .string()
        .trim()
        .required(
          `${lt('cdb_profile_new_password')} ${lt('cdb_admin_required_text')}`
        )
        .matches(PASSWORD_REGEX, lt('cdb_admin_form_verification_pwd_format')),
      confirmPass: yup
        .string()
        .trim()
        .oneOf(
          [yup.ref('pass'), null],
          lt('cdb_admin_form_verification_pwd_match')
        )
        .required(
          `${lt('cdb_profile_confirm_password')} ${lt(
            'cdb_admin_required_text'
          )}`
        ),
    })
    .required();

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<PasswordForm>({
    resolver: yupResolver(schema),
    defaultValues: defaultFormValue,
  });

  const submitForm = (formData: PasswordForm) => {
    const tmpToken = localStorage.getItem(TEMPORARY) ?? '';
    loginPassword({ newPassword: formData.pass?.trim(), tmpToken })
      .unwrap()
      .then((resp: LoginResponse) => {
        // completed → land; pending → next gate via the shared step-router
        // (routes on `next`; gate order is BE-driven). See login-step.ts.
        advanceLoginStep({
          resp,
          navigate,
          dispatch,
          redirect: searchParams.get(REDIRECT_SEARCH_PARAM_KEY),
          account,
        });
      })
      .catch(responseError);
  };

  const toastDanger = (msg: string, titleKey: string) => {
    dispatch(
      addToast({
        type: ToastType.Danger,
        msg,
        title: (<div className="letter-space3">{lt(titleKey)}</div>) as any,
      })
    );
  };

  // Error consumption reads the KRATOS body (DEF-005-01, batch 004 / ADR-1).
  // This page used to end with `const errMsg = error?.data?.error?.detail` — the
  // legacy BFF envelope. The endpoint moved to POST /v1/login/password back in the
  // multistep-login work but this reader did not, so `detail` has been `undefined`
  // for every error since, and the trailing `if (errMsg)` meant the body was
  // dropped in silence. The 422 arm was the worst of it: it deliberately fell
  // through without a toast, expecting `detail` to carry the reason, so a
  // server-side policy rejection told the user nothing whatsoever.
  //
  // mapRequestError is the paradigm 005 established (profile.utils.ts): it reads
  // {code, message, metadata.key} and returns frozen local copy for the pairs that
  // had legacy wording, falling back to the server's own text. Exactly one toast
  // per failure — no field-level setError here, because this form's fields are
  // `pass`/`confirmPass` and do not carry contract names to map onto.
  const responseError = (error: any) => {
    const { i18nKey, text } = mapRequestError(error);
    const mapped = i18nKey ? lt(i18nKey) : text ?? '';

    switch (error?.status) {
      case 401:
        // tmp step token expired/invalid → restart login.
        toastDanger(
          lt('auth_mfa_send_code_401_error_text'),
          'cdb_message_status_failed_text'
        );
        navigate('/login', { state: { account } });
        return;
      case 422:
        // Password policy rejected server-side → stay on the form and say why.
        toastDanger(mapped, 'cdb_message_status_error_text');
        return;
      default:
        toastDanger(
          mapped || lt('cdb_message_status_failed_text'),
          'cdb_message_status_failed_text'
        );
    }
  };

  const detectPasswordValidity = (isSubmitting: boolean) => {
    setIsSubmitting(isSubmitting);
    setCurrentPass(getValues('pass'));
  };

  return (
    <LoginBasicBox>
      <div className={cx([styles.container])}>
        <header
          className={cx([
            'd-flex flex-row align-items-center justify-content-start',
            'header',
          ])}
          style={{ color: 'gray' }}>
          <div
            className={cx(['back', 'd-flex align-items-center'])}
            onClick={() => {
              navigate('/login');
            }}>
            <span className={cx(['iconfont icon-ico-expand', 'arrowIcon'])} />
            &nbsp;
            <span>{lt('common_back')}</span>
          </div>
        </header>
        <main className={cx([styles.main])}>
          <section className={cx([styles.info])}>
            <section className={cx([styles.title])}>
              <span className="title-bold">
                {lt('auth_reset_password_title')}
              </span>
            </section>
            <Form noValidate validated={!_.isEmpty(errors)}>
              <Form.Group
                className={cx(['mb-4', styles.formRow])}
                controlId="pass">
                <Controller
                  name="pass"
                  control={control}
                  render={({ field }) => (
                    <PasswordInput
                      {...field}
                      onKeyUp={() => {
                        detectPasswordValidity(false);
                      }}
                      className={cx([styles.formRowInput])}
                      placeholder={lt('cdb_profile_new_password')}
                      isInvalid={!!errors.pass}
                    />
                  )}
                />
                {errors.pass && (
                  <Form.Control.Feedback
                    type="invalid"
                    className={cx(['invalid-feedback'])}>
                    <span>{errors?.pass.message}</span>
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              {currentPass && (
                <section className={cx([styles.passwordFactors])}>
                  <div className={cx(['mb-3', 'createPasswordText'])}>
                    {lt('auth_reset_create_password_text')}
                  </div>
                  <PasswordStrengthIndicator
                    password={currentPass}
                    showError={isSubmitting}
                  />
                </section>
              )}
              <Form.Group
                controlId="confirmPass"
                className={cx(['mb-4', styles.formRow])}>
                <Controller
                  name="confirmPass"
                  control={control}
                  render={({ field }) => (
                    <PasswordInput
                      {...field}
                      className={cx([styles.formRowInput])}
                      placeholder={lt('cdb_confirm_new_password')}
                      isInvalid={!!errors.confirmPass}
                    />
                  )}
                />
                {errors.confirmPass && (
                  <Form.Control.Feedback
                    type="invalid"
                    className={cx(['invalid-feedback'])}>
                    <span>{errors?.confirmPass.message}</span>
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Form>
          </section>
          <section className={cx([styles.actions])}>
            <Button
              onClick={e => {
                handleSubmit(submitForm)(e);
                detectPasswordValidity(true);
              }}
              className={cx(['title-semiBold', styles.loginButton])}>
              {isLoading && (
                <span>
                  <span
                    className="spinner-border spinner-border"
                    role="status"
                    aria-hidden="true"
                  />
                  &nbsp;&nbsp;
                </span>
              )}
              <span>{lt('auth_reset_button_reset')}</span>
            </Button>
          </section>
        </main>
      </div>
    </LoginBasicBox>
  );
};

export default ResetPass;
